'use client';

import type { GetResponseType } from '@/api/types/api';
import type { ApiResponseDefinition } from '@/api/types/api-definition';
import type { Currency, User } from '@/api/types/public';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import type {
  CoatingItem,
  ColorItem,
  LensIndexItem,
  LensShowResponse,
} from '@/app/_lens-components/types';
import { ELensViewAreaType } from '@/app/_lens-components/types';
import {
  handleColorItemPriceInfo,
  handleLensIndexPriceInfo,
  pickDefaultCoatingItem,
  shouldApplyCoatingSelection,
} from '@/app/_lens-components/utils';
import {
  getLensTypeKey,
  resolveLensTypeColorSelection,
} from '@/app/_lens-components/utils/applyDefaultLensType';
import {
  readPrevLensSelection,
  getPrevLensTypeSelectionTriple,
  decodePrevLensIndexKey,
  type PrevLensSelection,
} from '@/app/_lens-components/utils/operatePrevLensSelection';
import { add } from '@/utils/math';
import type { Dispatch, SetStateAction } from 'react';

export type ProgressiveViewAreaState = GetResponseType<
  ApiResponseDefinition['GET:/api/lens/view_area']
>['view_area'];

function isLensIndexRecommended(item: LensIndexItem): boolean {
  return Number(item.recommend) === 1;
}

function normalizeRefractiveIndex(value?: string): string {
  return (value ?? '').trim();
}

function normalizeIndexBrand(value?: string): string {
  return (value ?? '').trim().toLowerCase();
}

/** 按「折射率 + 品牌」判断是否为同一规格（产品：默认选中按镜片折射率+镜片品牌） */
function isSameLensIndexSpec(
  item: Pick<LensIndexItem, 'refractive_index' | 'index_brand'>,
  refractiveIndex: string,
  indexBrand?: string,
): boolean {
  if (
    normalizeRefractiveIndex(item.refractive_index) !== normalizeRefractiveIndex(refractiveIndex)
  ) {
    return false;
  }
  const savedBrand = normalizeIndexBrand(indexBrand);
  if (!savedBrand) return true;
  return normalizeIndexBrand(item.index_brand) === savedBrand;
}

/** 在当前列表中匹配折射率项：先 id，再 refractive_index + index_brand */
function findLensIndexInList(
  list: LensIndexItem[],
  target: {
    id?: number | string;
    refractive_index?: string;
    index_brand?: string;
  },
): LensIndexItem | undefined {
  if (target.id != null) {
    const byId = list.find((x) => `${x.id}` === `${target.id}`);
    if (byId) return byId;
  }
  if (target.refractive_index) {
    return list.find((x) =>
      isSameLensIndexSpec(x, target.refractive_index ?? '', target.index_brand),
    );
  }
  return undefined;
}

function findLensIndexByPrevKey(
  list: LensIndexItem[],
  prevLensIndexKey: string,
): LensIndexItem | undefined {
  const { refractiveIndex, indexBrand } = decodePrevLensIndexKey(prevLensIndexKey);
  if (!refractiveIndex) return undefined;
  return list.find((x) => isSameLensIndexSpec(x, refractiveIndex, indexBrand));
}

/** 默认折射率：① 上次选项（折射率+品牌）② 推荐项 ③ 首项 */
function pickDefaultLensIndexItem(list: LensIndexItem[], prev: PrevLensSelection): LensIndexItem {
  const fromPrev = findLensIndexByPrevKey(list, prev.prevLensIndexKey);
  if (fromPrev) return fromPrev;
  const recommended = list.find(isLensIndexRecommended);
  if (recommended) return recommended;
  return list[0];
}

export interface ApplyDefaultSelectionInits {
  /** 成功写入颜色后返回该项，便于同帧内接着跑折射率（避免订阅尚未更新） */
  initDefaultLensTypeSelection: (lensTypeColorData: LensShowResponse) => ColorItem | null;
  /** 成功写入折射率后返回该项，便于同帧内接着跑视野；同 id 或同「折射率+品牌」则保留，否则按「上次 key → 推荐 → 首项」重选 */
  initDefaultLensIndexSelection: (colorItemJustSet?: ColorItem | null) => LensIndexItem | null;
  initDefaultLensViewAreaSelection: (params: {
    progressiveViewArea: ProgressiveViewAreaState | null;
    selectedLensViewAreaType: ELensViewAreaType | null;
    setSelectedLensViewAreaType: Dispatch<SetStateAction<ELensViewAreaType | null>>;
    currency: Currency | null;
    userInfo: User;
    /** 同帧内上一步默认折射率，避免闭包未更新 */
    lensIndexItemJustSet?: LensIndexItem | null;
    /** 同帧内默认颜色，避免闭包未更新 */
    colorItemJustSet?: ColorItem | null;
  }) => void;
  initDefaultCoatingSelection: (params: {
    coatingList: CoatingItem[];
    currency: Currency | null;
    userInfo: User;
    isFrameProcess?: boolean;
  }) => void;
}

/**
 * 小语种默认勾选：返回四个初始化函数，供组件在 `hasDefaultSelection` 与数据就绪时调用。
 * store 用 selector 订阅；同帧串联时需传入上一步返回值（颜色 → 折射率 → 视野），见 ClientPage `getLensShow`。
 */
export function useApplyDefaultSelection(): ApplyDefaultSelectionInits {
  const selectedColorItem = useLensProcessStore((s) => s.selectedColorItem);
  const selectedLensIndexItem = useLensProcessStore((s) => s.selectedLensIndexItem);
  const selectedCoating = useLensProcessStore((s) => s.selectedCoating);
  const prescriptionType = useLensProcessStore((s) => s.prescriptionType);
  const setBrandExpand = useLensProcessStore((s) => s.setBrandExpand);
  const setSelectedBrand = useLensProcessStore((s) => s.setSelectedBrand);
  const setSelectedLensType = useLensProcessStore((s) => s.setSelectedLensType);
  const setSelectedColorItem = useLensProcessStore((s) => s.setSelectedColorItem);
  const setSelectedLensIndexItem = useLensProcessStore((s) => s.setSelectedLensIndexItem);
  const setSelectedAdvancedLensIndexItem = useLensProcessStore(
    (s) => s.setSelectedAdvancedLensIndexItem,
  );
  const setSelectedCoating = useLensProcessStore((s) => s.setSelectedCoating);
  function initDefaultLensTypeSelection(lensTypeColorData: LensShowResponse): ColorItem | null {
    const canInit = !!lensTypeColorData?.length && !selectedColorItem;
    if (!canInit) return null;

    const prev = readPrevLensSelection();
    const { lensTypeKey, brandTitle, lensColor } = getPrevLensTypeSelectionTriple(prev);
    const resolved =
      resolveLensTypeColorSelection(lensTypeColorData, lensTypeKey, brandTitle, lensColor) ||
      resolveLensTypeColorSelection(lensTypeColorData, '', '', '');
    if (!resolved?.color) return null;

    const { lens, brand, color } = resolved;
    if (brand) {
      const expandKey = getLensTypeKey(lens);
      setBrandExpand({ [expandKey]: true });
      if (lens.merge_lens_type) {
        lens.sd_lens_type = brand.lens_type;
      }
      lens.default_brand_title = brand.brand_title;
      setSelectedBrand(brand);
    } else {
      setSelectedBrand(null);
      setBrandExpand({});
    }
    lens.default_lens_color = color.lens_color;
    setSelectedLensType(lens);
    setSelectedColorItem(color);
    return color;
  }

  function initDefaultLensIndexSelection(
    colorItemJustSet?: ColorItem | null,
  ): LensIndexItem | null {
    const { selectedColorItem: colorInStore, selectedLensIndexItem: indexInStore } =
      useLensProcessStore.getState();
    const colorItem = colorItemJustSet ?? colorInStore;
    const list = colorItem?.lens_list;
    if (!list?.length) return null;

    const current = indexInStore;
    const matchedCurrent = current != null ? findLensIndexInList(list, current) : undefined;
    if (matchedCurrent) {
      if (matchedCurrent !== current) {
        setSelectedLensIndexItem(matchedCurrent);
      }
      return matchedCurrent;
    }

    const prev = readPrevLensSelection();
    const pick = pickDefaultLensIndexItem(list, prev);
    if (pick) {
      setSelectedLensIndexItem(pick);
      return pick;
    }
    return null;
  }

  function initDefaultLensViewAreaSelection(params: {
    progressiveViewArea: ProgressiveViewAreaState | null;
    selectedLensViewAreaType: ELensViewAreaType | null;
    setSelectedLensViewAreaType: Dispatch<SetStateAction<ELensViewAreaType | null>>;
    currency: Currency | null;
    userInfo: User;
    lensIndexItemJustSet?: LensIndexItem | null;
    colorItemJustSet?: ColorItem | null;
  }): void {
    const {
      progressiveViewArea,
      selectedLensViewAreaType,
      setSelectedLensViewAreaType,
      currency,
      userInfo,
      lensIndexItemJustSet,
      colorItemJustSet,
    } = params;
    const lensIdx = lensIndexItemJustSet ?? selectedLensIndexItem;
    const colorForPrice = colorItemJustSet ?? selectedColorItem;

    const canInit =
      !!progressiveViewArea &&
      !!lensIdx &&
      !selectedLensViewAreaType &&
      !!colorForPrice &&
      !!currency;
    if (!canInit || !currency) return;

    const prev = readPrevLensSelection();
    const prevAreaRaw = prev.prevViewArea;
    const advanced = lensIdx.advanced;

    const hasStoredViewAreaPreference =
      prevAreaRaw === ELensViewAreaType.ADVANCED || prevAreaRaw === ELensViewAreaType.NORMAL;

    if (hasStoredViewAreaPreference && (prevAreaRaw !== ELensViewAreaType.ADVANCED || advanced)) {
      setSelectedLensViewAreaType(prevAreaRaw as ELensViewAreaType);
      setSelectedAdvancedLensIndexItem(
        prevAreaRaw === ELensViewAreaType.ADVANCED ? advanced : null,
      );
      return;
    }

    // 本地未记录过视野：无升级档则普通；有两档则选总价更高者（同价优先升级档）
    if (!advanced) {
      setSelectedLensViewAreaType(ELensViewAreaType.NORMAL);
      setSelectedAdvancedLensIndexItem(null);
      return;
    }

    const normalLensP = handleLensIndexPriceInfo(lensIdx, currency, userInfo);
    const normalColorP = handleColorItemPriceInfo(colorForPrice, false, currency, userInfo);
    const advLensP = handleLensIndexPriceInfo(advanced, currency, userInfo);
    const advColorP = handleColorItemPriceInfo(advanced, false, currency, userInfo);
    const nTot = add(
      parseFloat(normalLensP.sell_price || '0'),
      parseFloat(normalColorP.sell_price || '0'),
    );
    const aTot = add(
      parseFloat(advLensP.sell_price || '0'),
      parseFloat(advColorP.sell_price || '0'),
    );
    if (aTot.gte(nTot)) {
      setSelectedLensViewAreaType(ELensViewAreaType.ADVANCED);
      setSelectedAdvancedLensIndexItem(advanced);
    } else {
      setSelectedLensViewAreaType(ELensViewAreaType.NORMAL);
      setSelectedAdvancedLensIndexItem(null);
    }
  }

  function initDefaultCoatingSelection(params: {
    coatingList: CoatingItem[];
    currency: Currency | null;
    userInfo: User;
    isFrameProcess?: boolean;
  }): void {
    const { coatingList, currency, userInfo, isFrameProcess } = params;
    const colorItem = useLensProcessStore.getState().selectedColorItem;
    const canInit =
      !!coatingList?.length &&
      !selectedCoating &&
      !!currency &&
      shouldApplyCoatingSelection(prescriptionType, colorItem, { isFrameProcess });
    if (!canInit || !currency) return;

    const prev = readPrevLensSelection();
    const pick = pickDefaultCoatingItem(coatingList, currency, userInfo, prev.prevCoatingId);
    if (pick) setSelectedCoating(pick);
  }
  return {
    initDefaultLensTypeSelection,
    initDefaultLensIndexSelection,
    initDefaultLensViewAreaSelection,
    initDefaultCoatingSelection,
  };
}
